import { CategoryBadge, CATEGORIES, catSizes } from '../lib/categories';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, fcfa } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Plus, Radio, Gavel, Package, ShoppingBag, CheckCircle, BarChart3, StopCircle, PlayCircle, Pencil, Trash2, XCircle, Store } from 'lucide-react';
import { SalesList } from '../components/SalesList';
import { PhotoUploader } from '../components/PhotoUploader';
import { ShopProfile } from '../components/ShopProfile';
import { imgSrc } from '../lib/cart';
import { Link, useSearchParams } from 'react-router-dom';
import { LiveStatsModal } from '../components/LiveStatsModal';
import { SellerTutorial } from '../components/SellerTutorial';
import WebRTCBroadcaster from '../components/WebRTCBroadcaster';
import { CommissionPanel } from '../components/CommissionPanel';
import { SellerPaymentInfo } from '../components/SellerPaymentInfo';
import { LiveAuctionPanel } from '../components/LiveAuctionPanel';

function SellerBroadcast({ liveId }) {
  return <WebRTCBroadcaster liveId={liveId} />;
}

export default function SellerDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [lives, setLives] = useState([]);
  const [sales, setSales] = useState([]);
  const [params] = useSearchParams();
  const [showProd, setShowProd] = useState(params.get('add') === '1');
  const [showLive, setShowLive] = useState(false);
  const [showAuc, setShowAuc] = useState(null); // live_id
  const [prod, setProd] = useState({ name: '', description: '', image_url: '', images: [], starting_price: '', price: '', stock: '', category: 'baskets', size: '', brand: '' });
  const [editing, setEditing] = useState(null);
  const saveEdit = async (p) => {
    try { await api.put(`/products/${p.product_id}`, { price: Number(editing.price) || 0, stock: Number(editing.stock) || 0, starting_price: Number(editing.starting_price) || p.starting_price }); toast.success('Article mis à jour'); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const archive = async (p) => {
    if (!window.confirm(`Retirer « ${p.name} » de la boutique ?`)) return;
    try { await api.delete(`/products/${p.product_id}`); toast.success('Article retiré'); load(); } catch (e) { toast.error('Erreur'); }
  };
  const [live, setLive] = useState({ title: '', description: '', stream_url: '', thumbnail: '', stream_type: 'webrtc', category: 'baskets' });
  const [auc, setAuc] = useState({ product_id: '', starting_price: '', increment: '', duration_seconds: '' });
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState(params.get('tab') || 'articles');

  const endLive = async (live_id) => {
    if (!window.confirm('Terminer ce live ?')) return;
    try { const { data } = await api.post(`/lives/${live_id}/end`); setStats({ ...data.stats, title: lives.find((x) => x.live_id === live_id)?.title }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const showStats = async (live_id) => {
    try { const { data } = await api.get(`/lives/${live_id}/stats`); setStats(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const load = async () => {
    try {
      const [{ data: p }, { data: l }, { data: s }] = await Promise.all([
        api.get('/products/mine'),
        api.get('/lives', { params: { status: 'all' } }),
        api.get('/orders/sales'),
      ]);
      setProducts(p);
      setLives(l.filter((x) => x.seller_id === user?.user_id).sort((a, b) => (a.status === 'live' ? -1 : 1) - (b.status === 'live' ? -1 : 1)).slice(0, 12));
      setSales(s);
    } catch {}
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">{t('login')}</div></div>;
  if (user.role !== 'seller' && user.role !== 'admin') return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">Access denied</div></div>;

  const createProd = async (e) => {
    e.preventDefault();
    try { await api.post('/products', { ...prod, price: Number(prod.price) || 0, starting_price: Number(prod.starting_price) || 0, stock: Number(prod.stock) || 0 }); toast.success('Produit publié dans votre boutique'); setShowProd(false); setProd({ name: '', description: '', image_url: '', images: [], starting_price: '', price: '', stock: '', category: 'baskets', size: '', brand: '' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const createLive = async (e) => {
    e.preventDefault();
    try { await api.post('/lives', live); toast.success('OK'); setShowLive(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const launchAuc = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auctions', { ...auc, live_id: showAuc, starting_price: Number(auc.starting_price) || 0, increment: Number(auc.increment) || 500, duration_seconds: Number(auc.duration_seconds) || 60 });
      toast.success('OK'); setShowAuc(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const setSaleStatus = async (order_id, action) => {
    if (!action) { load(); return; }
    try { await api.post(`/orders/${order_id}/${action}`); toast.success('OK'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const shipOrder = async (order_id) => {
    const note = window.prompt('Message pour le client (optionnel) : livreur, délai, numéro de suivi…', '') ;
    if (note === null) return;
    try {
      const { data } = await api.post(`/orders/${order_id}/ship`, { note });
      toast.success('Commande marquée expédiée');
      if (data.whatsapp_url) window.open(data.whatsapp_url, '_blank', 'noopener');
      else toast.info("Pas de numéro client pour WhatsApp — le client est prévenu dans l'appli");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {!user.approved && user.role === 'seller' && (
          <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/40 text-amber-300 text-sm">
            {t('seller_pending')}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-3xl font-extrabold">{t('seller_dashboard')}</h1>
          <Link to="/comment-ca-marche" data-testid="demo-video-link" className="inline-flex items-center gap-2 text-sm font-semibold text-sky-300 hover:text-sky-200 rounded-full border border-sky-500/40 bg-sky-500/10 px-4 py-2">
            <PlayCircle className="h-4 w-4" />Vidéo guide : inscription, vente, achat
          </Link>
        </div>
        <LiveStatsModal stats={stats} onClose={() => setStats(null)} />
        {user.role === 'seller' && !user.shop_name ? (
          <div className="space-y-6" data-testid="shop-onboarding">
            <div className="p-5 rounded-2xl bg-rose-500/10 border border-rose-500/40">
              <div className="font-display text-xl font-bold text-white flex items-center gap-2"><Store className="h-5 w-5 text-rose-400" />Bienvenue ! Première étape : créez votre boutique</div>
              <p className="text-sm text-rose-100/80 mt-1">Avant d'ajouter des produits ou de lancer un live, donnez un nom et une ville à votre boutique. C'est ce nom que verront vos clients. Cela prend 30 secondes.</p>
            </div>
            <ShopProfile />
          </div>
        ) : (<>
        <div className="flex gap-2 border-b border-slate-800 overflow-x-auto" data-testid="seller-tabs">
          {[['articles', 'Ma boutique & mes produits', Store], ['live', 'Live', Radio], ['ventes', 'Mes ventes', ShoppingBag], ['aide', 'Aide & paiements', PlayCircle]].map(([k, label, Icon]) => (
            <button key={k} type="button" data-testid={`seller-tab-${k}`} onClick={() => setTab(k)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? 'border-rose-500 text-white' : 'border-transparent text-slate-400 hover:text-white'}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>
        {tab === 'aide' && <><SellerTutorial /><SellerPaymentInfo /></>}

        {tab === 'articles' && (
          <div className="grid sm:grid-cols-3 gap-3" data-testid="shop-steps">
            {[['1', 'Créez votre boutique', 'Nom, ville, description — juste en dessous.'], ['2', 'Ajoutez vos produits', 'Bouton « Ajouter un produit » : photos, prix, stock.'], ['3', 'Vendez', 'Vos produits apparaissent dans la Boutique et sur votre page publique.']].map(([n, tt, sub]) => (
              <div key={n} className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 flex gap-3">
                <span className="h-8 w-8 shrink-0 rounded-full btn-brand grid place-items-center text-white font-bold text-sm">{n}</span>
                <div><div className="font-bold text-sm">{tt}</div><div className="text-xs text-slate-400">{sub}</div></div>
              </div>
            ))}
          </div>
        )}
        {tab === 'articles' && <ShopProfile />}

        {/* Products */}
        {tab === 'articles' && <section className="card-elev rounded-2xl p-5 border-2 border-rose-500/30" id="mes-produits">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><Package className="h-5 w-5 text-rose-400" />Mes produits ({products.length})</h2>
              <p className="text-xs text-slate-400 mt-0.5">Un produit avec un prix de vente et un stock &gt; 0 est visible dans la Boutique. Prix 0 = enchère en live uniquement.</p>
            </div>
            <Button data-testid="btn-add-product" onClick={() => setShowProd((s) => !s)} size="lg" className="btn-brand text-white font-bold"><Plus className="h-5 w-5 mr-1" />{showProd ? 'Fermer' : 'Ajouter un produit'}</Button>
          </div>
          {products.length === 0 && !showProd && (
            <div className="mb-4 p-4 rounded-xl border border-dashed border-slate-700 text-center text-sm text-slate-400" data-testid="no-products-hint">
              Votre boutique est vide. Cliquez sur <strong className="text-white">« Ajouter un produit »</strong> pour publier votre premier article.
            </div>
          )}
          {showProd && (
            <form onSubmit={createProd} className="grid md:grid-cols-2 gap-3 mb-4">
              <input data-testid="prod-name" value={prod.name} onChange={(e) => setProd({ ...prod, name: e.target.value })} placeholder={t('product_name')}
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <input data-testid="prod-image_url" value={prod.image_url} onChange={(e) => setProd({ ...prod, image_url: e.target.value })} placeholder="URL d'image (optionnel si photos ci-dessous)"
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <PhotoUploader images={prod.images} onChange={(images) => setProd({ ...prod, images })} />
              <select data-testid="prod-category" value={prod.category} onChange={(e) => setProd({ ...prod, category: e.target.value, size: '' })}
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              {catSizes(prod.category).length > 0 ? (
                <select data-testid="prod-size" value={prod.size} onChange={(e) => setProd({ ...prod, size: e.target.value })}
                  className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none">
                  <option value="">Taille / Pointure</option>
                  {catSizes(prod.category).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input data-testid="prod-size" value={prod.size} onChange={(e) => setProd({ ...prod, size: e.target.value })} placeholder="Taille (optionnel)"
                  className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              )}
              <input data-testid="prod-brand" value={prod.brand} onChange={(e) => setProd({ ...prod, brand: e.target.value })} placeholder="Marque (Nike, Levi's…)"
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <label className="text-[11px] uppercase tracking-widest text-slate-400">Prix de vente boutique (FCFA, 0 = enchère seulement)
                <input type="number" data-testid="prod-sale-price" value={prod.price} placeholder="Ex : 15000" onChange={(e) => setProd({ ...prod, price: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              </label>
              <label className="text-[11px] uppercase tracking-widest text-slate-400">Prix de départ enchère (FCFA)
                <input type="number" data-testid="prod-price" value={prod.starting_price}
                  onChange={(e) => setProd({ ...prod, starting_price: e.target.value })} placeholder="Ex : 5000"
                  className="mt-1 w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              </label>
              <label className="text-[11px] uppercase tracking-widest text-slate-400">Stock
                <input type="number" min="0" data-testid="prod-stock" value={prod.stock} placeholder="Ex : 3" onChange={(e) => setProd({ ...prod, stock: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              </label>
              <input data-testid="prod-desc" value={prod.description} onChange={(e) => setProd({ ...prod, description: e.target.value })} placeholder={t('description')}
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <Button data-testid="prod-submit" type="submit" className="md:col-span-2 bg-emerald-600 hover:bg-emerald-500 text-white">Publier le produit</Button>
            </form>
          )}
          <div className="grid md:grid-cols-3 gap-3">
            {products.map((p) => (
              <div key={p.product_id} data-testid={`prod-${p.product_id}`} className="rounded-xl bg-slate-900/60 border border-slate-800 overflow-hidden">
                {p.image_url && <img src={imgSrc(p.image_url)} alt="" className="h-32 w-full object-cover" />}
                <div className="p-3 space-y-1">
                  <div className="font-bold truncate">{p.name}</div>
                  <CategoryBadge category={p.category} size={p.size} brand={p.brand} className="mt-1" />
                  {p.price > 0 && p.stock > 0 ? <div className="text-[10px] uppercase tracking-widest text-emerald-400">Visible en boutique</div> : p.price > 0 ? <div className="text-[10px] uppercase tracking-widest text-rose-400">Rupture — non visible</div> : null}
                  {editing?.id === p.product_id ? (
                    <div className="grid grid-cols-3 gap-1 text-xs" data-testid={`prod-edit-${p.product_id}`}>
                      <label className="text-slate-400">Prix<input type="number" data-testid={`edit-price-${p.product_id}`} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="w-full px-2 py-1 rounded bg-slate-950 border border-slate-700 text-white" /></label>
                      <label className="text-slate-400">Stock<input type="number" data-testid={`edit-stock-${p.product_id}`} value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="w-full px-2 py-1 rounded bg-slate-950 border border-slate-700 text-white" /></label>
                      <label className="text-slate-400">Enchère<input type="number" value={editing.starting_price} onChange={(e) => setEditing({ ...editing, starting_price: e.target.value })} className="w-full px-2 py-1 rounded bg-slate-950 border border-slate-700 text-white" /></label>
                      <Button size="sm" data-testid={`edit-save-${p.product_id}`} onClick={() => saveEdit(p)} className="col-span-2 bg-emerald-600 text-white">Enregistrer</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="border-slate-700 text-slate-200 bg-transparent">Annuler</Button>
                    </div>
                  ) : (
                    <>
                      <div className="text-amber-400 font-mono-num text-sm">{p.price > 0 ? <>{fcfa(p.price)} <span className="text-slate-500">· stock {p.stock ?? 0}</span></> : <span className="text-slate-400">Enchère uniquement · départ {fcfa(p.starting_price)}</span>}</div>
                      {p.sold && <div className="text-xs text-emerald-400">Vendu en enchère</div>}
                      <div className="flex gap-1 pt-1">
                        <Button size="sm" variant="outline" data-testid={`edit-${p.product_id}`} onClick={() => setEditing({ id: p.product_id, price: p.price || 0, stock: p.stock ?? 0, starting_price: p.starting_price })} className="border-slate-700 text-slate-200 bg-transparent"><Pencil className="h-3 w-3 mr-1" />Modifier</Button>
                        <Button size="sm" variant="outline" data-testid={`archive-${p.product_id}`} onClick={() => archive(p)} className="border-rose-500/40 text-rose-300 bg-transparent"><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>}

        {/* Lives */}
        {tab === 'live' && <section className="card-elev rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Radio className="h-5 w-5 text-rose-400" />{t('my_lives')}</h2>
            <Button data-testid="btn-start-live" onClick={() => setShowLive((s) => !s)} className="btn-brand text-white"><Plus className="h-4 w-4 mr-1" />{t('start_live')}</Button>
          </div>
          {showLive && (
            <form onSubmit={createLive} className="grid md:grid-cols-2 gap-3 mb-4">
              <input data-testid="live-title" value={live.title} onChange={(e) => setLive({ ...live, title: e.target.value })} placeholder={t('live_title')}
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <select data-testid="live-category" value={live.category} onChange={(e) => setLive({ ...live, category: e.target.value })}
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/30 text-sky-200 text-xs" data-testid="live-platform-note">
                📷 Le live se déroule directement sur JOTALY avec la caméra de votre téléphone ou ordinateur. Après création, cliquez « Démarrer la diffusion ».
              </div>
              <input data-testid="live-thumb" value={live.thumbnail} onChange={(e) => setLive({ ...live, thumbnail: e.target.value })} placeholder="Thumbnail URL"
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <input data-testid="live-desc" value={live.description} onChange={(e) => setLive({ ...live, description: e.target.value })} placeholder={t('description')}
                className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
              <Button data-testid="live-submit" type="submit" className="md:col-span-2 bg-emerald-600 hover:bg-emerald-500 text-white">{t('create')}</Button>
            </form>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {lives.map((l) => (
              <div key={l.live_id} className="rounded-xl bg-slate-900/60 border border-slate-800 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-bold truncate">{l.title}</div>
                  <div className="text-xs text-rose-400 font-bold">{l.status === 'live' ? t('live_now') : t('ended')}</div>
                </div>
                <div className="text-xs text-slate-400 truncate">{l.stream_type === 'webrtc' ? '📷 Caméra WebRTC' : l.stream_url}</div>
                {l.status === 'live' && l.stream_type === 'webrtc' && (
                  <div className="mt-3"><SellerBroadcast liveId={l.live_id} /></div>
                )}
                {l.status === 'live' && <LiveAuctionPanel liveId={l.live_id} />}
                <div className="flex flex-wrap gap-2 mt-2">
                  {l.status === 'live' && (
                    <Button data-testid={`launch-auc-${l.live_id}`} onClick={() => setShowAuc(showAuc === l.live_id ? null : l.live_id)}
                      size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
                      <Gavel className="h-4 w-4 mr-1" />{t('launch_auction')}
                    </Button>
                  )}
                  {l.status === 'live' && (
                    <Button data-testid={`end-live-${l.live_id}`} onClick={() => endLive(l.live_id)} size="sm" variant="outline" className="border-rose-500/50 text-rose-300 bg-transparent hover:bg-rose-500/10">
                      <StopCircle className="h-4 w-4 mr-1" />Terminer le live
                    </Button>
                  )}
                  <Button data-testid={`live-stats-${l.live_id}`} onClick={() => showStats(l.live_id)} size="sm" variant="outline" className="border-slate-700 text-slate-200 bg-transparent">
                    <BarChart3 className="h-4 w-4 mr-1" />Statistiques
                  </Button>
                </div>
                {showAuc === l.live_id && (
                  <form onSubmit={launchAuc} className="mt-3 space-y-2">
                    <select data-testid="auc-product" value={auc.product_id} onChange={(e) => setAuc({ ...auc, product_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none">
                      <option value="">{t('product_name')}</option>
                      {products.filter((p) => !p.sold).map((p) => <option key={p.product_id} value={p.product_id}>{p.name}{p.size ? ` · T.${p.size}` : ''}</option>)}
                    </select>
                    <div className="grid grid-cols-3 gap-2">
                      <label className="text-[11px] uppercase tracking-widest text-slate-400">Prix de départ (FCFA)
                        <input type="number" data-testid="auc-price" value={auc.starting_price} onChange={(e) => setAuc({ ...auc, starting_price: e.target.value })}
                          className="mt-1 w-full px-2 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" placeholder={t('starting_price')} />
                      </label>
                      <label className="text-[11px] uppercase tracking-widest text-slate-400">Palier (+FCFA / mise)
                        <input type="number" data-testid="auc-incr" value={auc.increment} onChange={(e) => setAuc({ ...auc, increment: e.target.value })}
                          className="mt-1 w-full px-2 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" placeholder={t('increment')} />
                      </label>
                      <label className="text-[11px] uppercase tracking-widest text-slate-400">Durée (secondes)
                        <input type="number" data-testid="auc-dur" value={auc.duration_seconds} onChange={(e) => setAuc({ ...auc, duration_seconds: e.target.value })}
                          className="mt-1 w-full px-2 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" placeholder={t('duration')} />
                      </label>
                    </div>
                    <Button data-testid="auc-submit" type="submit" size="sm" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"><Gavel className="h-4 w-4 mr-1" />Lancer l'enchère</Button>
                  </form>
                )}
              </div>
            ))}
          </div>
        </section>}

        {/* Sales */}
        {tab === 'ventes' && <section className="space-y-4">
          <div><div className="eyebrow">Espace vendeur</div><h2 className="font-display text-3xl font-extrabold flex items-center gap-2"><ShoppingBag className="h-7 w-7 text-[#E8B84A]" />Mes ventes</h2><div className="gold-line mt-4" /></div>
          <SalesList sales={sales} onAction={setSaleStatus} onShip={shipOrder} />
        </section>}

        {tab === 'ventes' && <CommissionPanel />}
        </>)}
      </div>
    </div>
  );
}
